import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Divider, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useTranslation } from 'react-i18next';
import { aiService, type AiFixRecommendation } from '@services/aiService';
import { apiErrorCode, toDisplayError } from '../../utils/apiError';
import AiOffNote from '../ai/AiOffNote';

interface AiFixSuggestionProps {
  orgId: string;
  findingId: string;
}

/**
 * REV-513: an optional AI-written fix for this specific element, offered
 * alongside the rule's generic guidance.
 *
 * Renders NOTHING when the organization has no AI key. A disabled button would
 * be worse than absent: most members cannot set the key, so it would be
 * permanent clutter advertising a feature they cannot turn on. Admins discover
 * it in the Integrations page instead.
 *
 * Nothing is requested on mount. Every generation costs the organization money,
 * so it happens only when a user asks for it, once per click.
 */
export default function AiFixSuggestion({ orgId, findingId }: AiFixSuggestionProps) {
  const { t } = useTranslation();
  const [available, setAvailable] = useState(false);
  // REV-1127: an organization with AI switched off is told so, not left to
  // wonder where the suggestion went.
  const [aiOff, setAiOff] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState<AiFixRecommendation | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const status = await aiService.getCredentialStatus(orgId);
        if (!cancelled) {
          setAvailable(status.configured);
          setAiOff(status.aiEnabled === false);
        }
      } catch {
        // Not being able to tell means we do not offer it. Surfacing an error
        // for an optional extra would be noise on a page about a finding.
        if (!cancelled) setAvailable(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  const generate = useCallback(async () => {
    setGenerating(true);
    setError(null);
    try {
      setResult(await aiService.generateFixRecommendation(orgId, findingId));
    } catch (err) {
      // The status said "configured" when this mounted, but an admin may have
      // removed the key since. "Try again" would be the wrong advice.
      setError(
        apiErrorCode(err) === 'no_credential'
          ? t(
              'findingDetail.ai.noKey',
              'This organization no longer has an AI key configured. Ask an organization admin to add one.'
            )
          : // REV-694: the API's own reason. "Please try again" is right for a
            // rate limit or an outage, and wrong for a key the provider
            // refuses, which fails the same way until an admin replaces it.
            // REV-1127: ai_disabled (switched off since this page loaded) is
            // one of those reasons; its catalog text says who can turn AI on.
            toDisplayError(
              err,
              t(
                'findingDetail.ai.failed',
                'The suggestion could not be generated. Please try again.'
              )
            )
      );
    } finally {
      setGenerating(false);
    }
  }, [orgId, findingId, t]);

  // REV-1127: switched off by an Owner or Admin. Unlike a missing key, this is
  // a decision the organization made, so the place the feature would be says
  // so, whether or not a key is configured.
  if (aiOff) {
    return (
      <Box sx={{ mt: 3 }}>
        <Divider sx={{ mb: 2 }} />
        <Typography variant="subtitle2" component="h3" color="text.secondary" gutterBottom>
          {t('findingDetail.ai.title', 'AI suggestion for this element')}
        </Typography>
        <AiOffNote />
      </Box>
    );
  }

  if (!available) {
    return null;
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Divider sx={{ mb: 2 }} />
      <Typography variant="subtitle2" component="h3" color="text.secondary" gutterBottom>
        {t('findingDetail.ai.title', 'AI suggestion for this element')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {t(
          'findingDetail.ai.blurb',
          'Uses your organization’s own AI key to suggest a fix for this specific element.'
        )}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Button
        variant="outlined"
        startIcon={
          generating ? (
            <CircularProgress size={18} color="inherit" aria-hidden="true" />
          ) : (
            <AutoAwesomeIcon />
          )
        }
        onClick={generate}
        // Disabled only while a request is in flight — every click costs money,
        // so a double-submit is a real cost, not just a duplicate render.
        disabled={generating}
        aria-busy={generating}
      >
        {generating
          ? t('findingDetail.ai.generating', 'Generating…')
          : result
            ? t('findingDetail.ai.regenerate', 'Generate again')
            : t('findingDetail.ai.generate', 'Generate AI suggestion')}
      </Button>

      {/* role=status: the suggestion lands well below the button that produced
          it, so without a live region a screen-reader user gets no signal that
          anything happened. */}
      <Box role="status" aria-live="polite" sx={{ mt: 2 }}>
        {result && (
          <Box
            sx={{
              p: 2,
              borderRadius: 1,
              border: 1,
              borderColor: 'divider',
              bgcolor: 'action.hover',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              <Chip
                size="small"
                color="default"
                label={t('findingDetail.ai.badge', 'AI-generated')}
              />
              {/* Invariant 3 — humans attest, AI assists. Unlabelled, this is
                  one paste away from appearing in an audit as verified fact. */}
              <Typography variant="caption" color="text.secondary">
                {t(
                  'findingDetail.ai.unverified',
                  'This has not been verified by a person. Check it before acting on it.'
                )}
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {result.recommendation}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
